import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell, GoogleIcon } from "@/features/auth/AuthShell";
import { applySignedInUser, metaQuery, safeRedirect, toAuthUser } from "@/features/auth/session";
import { authClient } from "@/lib/auth-client";

const searchSchema = z.object({
  redirect: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/signup")({
  validateSearch: (s) => searchSchema.parse(s),
  beforeLoad: ({ context, search }) => {
    if (context.user) throw redirect({ href: safeRedirect(search.redirect) });
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(metaQuery()),
  component: SignupPage,
});

function SignupPage() {
  const search = Route.useSearch();
  const { data: meta } = useQuery(metaQuery());
  const qc = useQueryClient();
  const router = useRouter();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const destination = safeRedirect(search.redirect);
  const first = meta ? !meta.hasUsers : false;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    const { data, error: err } = await authClient.signUp.email({ name, email, password });
    setPending(false);
    if (err || !data?.user) {
      setError(err?.message ?? "Sign-up failed");
      return;
    }
    await applySignedInUser(qc, toAuthUser(data.user));
    await router.invalidate();
    await router.navigate({ href: destination });
  };

  const google = () =>
    authClient.signIn.social({
      provider: "google",
      callbackURL: destination,
      errorCallbackURL: "/login",
    });

  return (
    <AuthShell
      title={first ? "Create the first account" : "Create your account"}
      subtitle={
        first
          ? "This account becomes the admin and can invite everyone else."
          : "Sign-ups are invite-only. Use the email address an admin invited."
      }
      footer={
        <>
          Already have an account?{" "}
          <Link
            to="/login"
            search={{ redirect: search.redirect }}
            className="text-foreground underline underline-offset-4"
          >
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4" data-testid="signup-form">
        <div className="grid gap-1.5">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            autoComplete="name"
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="signup-name"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="signup-email"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="signup-password"
          />
          <p className="text-xs text-muted-foreground">At least 8 characters.</p>
        </div>
        {error && (
          <p
            className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
            role="alert"
            data-testid="auth-error"
          >
            {error}
          </p>
        )}
        <Button type="submit" disabled={pending} data-testid="signup-submit">
          {first ? "Create admin account" : "Create account"}
        </Button>
        {meta?.googleEnabled && (
          <>
            <div className="relative text-center text-xs text-muted-foreground before:absolute before:inset-x-0 before:top-1/2 before:h-px before:bg-border">
              <span className="relative bg-background px-2">or</span>
            </div>
            <Button type="button" variant="outline" onClick={google} data-testid="signup-google">
              <GoogleIcon />
              Continue with Google
            </Button>
          </>
        )}
      </form>
    </AuthShell>
  );
}
